// TMOS Progress Bar Component
// Date: January 13, 2026
// Purpose: Display progress with customizable colors and labels

import React from 'react';

export interface ProgressBarProps {
  progress: number; // 0-100
  label?: string;
  showPercentage?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  height?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  label,
  showPercentage = true,
  color = 'primary',
  height = 'md',
  className = ''
}) => {
  const heightClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4'
  };

  const getColorClasses = () => {
    switch (color) {
      case 'primary':
        return 'bg-[var(--color-primary-text)]';
      case 'success':
        return 'bg-[var(--color-success)]';
      case 'warning':
        return 'bg-[var(--color-warning)]';
      case 'danger':
        return 'bg-[var(--color-error)]';
      case 'info':
        return 'bg-[var(--color-info)]';
      default:
        return 'bg-[var(--color-primary-text)]';
    }
  };

  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-[var(--color-secondary-text)]">{label}</span>
          {showPercentage && (
            <span className="text-sm font-semibold text-[var(--color-secondary-text)]">
              {Math.round(clampedProgress)}%
            </span>
          )}
        </div>
      )}
      <div className={`w-full ${heightClasses[height]} bg-[var(--color-border)] rounded-full overflow-hidden`}>
        <div
          className={`${getColorClasses()} h-full transition-all duration-300 rounded-full`}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
};

